import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import max from 'lodash/max';
import isEmpty from 'lodash/isEmpty';
import GenericDropdown from '../../../shared/common/GenericDropDown';
import { DataTable } from '../../../shared/common/DataTable';
import { HelpTooltip } from '../../../shared/common/HelpTooltip';
import { MATERIAL_ICON_CART } from '../../../shared/common/Button/Icon';
import { THEME_RED, themeColor } from '../../../styles/basics/colors';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { CreditSummaryOut } from '../../../store/apiLibx';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../../shared/utils/formatCurrency';

const StDropdownButton = styled.a`
    font-size: 16px !important;
`;

const StDropdownContainer = styled.div<{ movedLeftBy: number; isCalculated: boolean }>`
    width: 420px;
    padding: 20px !important;
    left: unset !important;
    top: 40px !important;
    right: ${props => `${-200 - props.movedLeftBy}px !important`};
    display: ${props => (props.isCalculated ? null : 'block !important')};
    visibility: ${props => (props.isCalculated ? null : 'hidden')};
`;

const StActiveArrow = styled.span<{ movedLeftBy: number }>`
    right: ${props => `${props.movedLeftBy + 240}px !important`};
`;

const StTopContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;

    .buy-more-credits-button {
        width: 150px;
    }
`;

const StSimpleTable = styled.table`
    width: 35%;
`;

const StSimpleTableCell = styled.td<{ alignRight?: boolean }>`
    padding: 3px 0 !important;
    text-align: ${props => (props.alignRight ? 'right' : 'initial')};
`;

const StTextarea = styled.textarea`
    box-sizing: border-box;
    width: 100%;
    height: 180px;
    font-size: 13px;
    border: 1px solid #ffffff;
    border-radius: 4px;
    margin-bottom: 25px;
    padding: 10px 14px 10px 14px;
    box-shadow: inset 0 0 4px 0 rgba(0, 0, 0, 0.26);
`;

const StButtonLine = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 35px;
`;

const StButtonWithMargin = styled(Button)`
    margin-left: 5px;
    width: 100px;
`;

const StBuyCreditsSection = styled.div`
    margin-top: 30px;
`;

const StInfoLine = styled.p`
    margin-top: 20px;
`;

const StSummaryTitleLine = styled.div`
    display: flex;
    line-height: 1;
`;

const StTooltip = styled(HelpTooltip)`
    margin-left: 5px;
`;

const StAvailableCreditLabel = styled.span<{ warning: boolean }>`
    font-weight: ${props => (props.warning ? 'bold' : 'initial')};
    color: ${props => (props.warning ? themeColor(THEME_RED) : 'initial')};
`;

interface Props {
    credits: CreditSummaryOut;
    purchaseCredits: (message: string) => void;
    currency: string;
    taxName: string;
}

export const CreditDropdown: FC<Props> = props => {
    const { t } = useTranslation();
    const dropdownContainerRef = useRef<HTMLDivElement>(null);

    const [message, setMessage] = useState('');
    const [buyMoreCreditsMode, setBuyMoreCreditsMode] = useState(false);
    const [isCalculated, setIsCalculated] = useState(false);
    const [movedLeftBy, setMovedLeftBy] = useState(0);

    useEffect(() => {
        const boundingRect = dropdownContainerRef?.current?.getBoundingClientRect() ?? { right: 0 };
        const movedLeftBy = window.innerWidth - Math.ceil(boundingRect.right);

        // isCalculated is necessary to switch from display none to visibility for calculation
        if (movedLeftBy <= 10) {
            setMovedLeftBy(movedLeftBy - 5);
        }
        setIsCalculated(true);
    }, []);

    function enableBuyMoreCreditsMode() {
        setBuyMoreCreditsMode(true);
    }

    function disableBuyMoreCreditsMode() {
        setBuyMoreCreditsMode(false);
        setMessage('');
    }

    function submitCreditsRequest() {
        props.purchaseCredits(message);
        setMessage('');
    }

    function handleChange(e) {
        setMessage(e.target.value);
    }

    const creditSummary = props.credits ?? {};
    const creditList = props.credits.creditRecordList ?? [];

    const isCreditAvailable = (creditSummary?.available ?? 0) > 0;

    return (
        <GenericDropdown>
            <StDropdownButton data-toggle-dropdown className="bt-dd">
                <span>
                    {t('yourLocalContent.yourTitles.availableCredits')}:{' '}
                    <StAvailableCreditLabel warning={!isCreditAvailable}>
                        {max([creditSummary.available, 0])}
                    </StAvailableCreditLabel>
                </span>
                <i className="material-icons md-up closed">keyboard_arrow_down</i>
                <i className="material-icons md-up opened">keyboard_arrow_up</i>
            </StDropdownButton>
            <StDropdownContainer
                className="default-dd"
                ref={dropdownContainerRef}
                movedLeftBy={movedLeftBy}
                isCalculated={isCalculated}
            >
                <StActiveArrow className="active-arrow" movedLeftBy={movedLeftBy} />
                <StTopContainer>
                    <StSimpleTable>
                        <thead>
                            <tr>
                                <StSimpleTableCell colSpan={2}>
                                    <StSummaryTitleLine>
                                        <b>{t('yourLocalContent.yourTitles.creditSummary')}</b>
                                        <StTooltip text={t('yourLocalContent.yourTitles.creditSummaryTooltip')} />
                                    </StSummaryTitleLine>
                                </StSimpleTableCell>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <StSimpleTableCell>{t('yourLocalContent.yourTitles.total')}</StSimpleTableCell>
                                <StSimpleTableCell alignRight={true}>
                                    <b>{creditSummary.total}</b>
                                </StSimpleTableCell>
                            </tr>
                            <tr>
                                <StSimpleTableCell>{t('yourLocalContent.yourTitles.inUse')}</StSimpleTableCell>
                                <StSimpleTableCell alignRight={true}>
                                    <b>{creditSummary.inUse}</b>
                                </StSimpleTableCell>
                            </tr>
                            <tr>
                                <StSimpleTableCell>
                                    <b>{t('yourLocalContent.yourTitles.available')}</b>
                                </StSimpleTableCell>
                                <StSimpleTableCell alignRight={true}>
                                    <b>{max([creditSummary.available, 0])}</b>
                                </StSimpleTableCell>
                            </tr>
                        </tbody>
                    </StSimpleTable>
                    <Button
                        icon={MATERIAL_ICON_CART}
                        size={ButtonSize.MEDIUM}
                        text={t('yourLocalContent.yourTitles.buyMoreCredits')}
                        disabled={buyMoreCreditsMode}
                        onClick={enableBuyMoreCreditsMode}
                        className="buy-more-credits-button"
                    />
                </StTopContainer>
                {buyMoreCreditsMode ? (
                    <StBuyCreditsSection>
                        <span>
                            <b>{t('yourLocalContent.yourTitles.buyCredits')}</b>
                        </span>
                        <StTextarea
                            value={message}
                            onChange={handleChange}
                            placeholder={t('yourLocalContent.yourTitles.placeholderBuyCredits')}
                        />
                        <StButtonLine>
                            <Button
                                onClick={event => {
                                    event.stopPropagation();
                                    disableBuyMoreCreditsMode();
                                }}
                                text={t('yourLocalContent.yourTitles.cancel')}
                                size={ButtonSize.MEDIUM}
                                theme={ButtonTheme.NEUTRAL}
                            />
                            <StButtonWithMargin
                                size={ButtonSize.MEDIUM}
                                text={t('yourLocalContent.yourTitles.sendOrder')}
                                disabled={isEmpty(message)}
                                onClick={submitCreditsRequest}
                            />
                        </StButtonLine>
                    </StBuyCreditsSection>
                ) : null}
                <DataTable
                    cellPaddingVertical={5}
                    cellPaddingHorizontal={10}
                    maxHeight={150}
                    columns={[
                        {
                            label: t('yourLocalContent.yourTitles.date'),
                            accessor: credit => t('dateFormat.date', { val: new Date(credit.date ?? 0) })
                        },
                        {
                            label: t('yourLocalContent.yourTitles.creditsPurchased'),
                            accessor: credit => t('yourLocalContent.yourTitles.credit', { count: credit.creditAmount })
                        },
                        {
                            label: t('yourLocalContent.yourTitles.totalExTax', { taxName: props.taxName }),
                            accessor: credit =>
                                t('numberFormat.numberWithCurrencyName', {
                                    val: FormatCurrency.convertIntegerToCurrencyFormat(credit.cost),
                                    currencyName: props.currency
                                })
                        }
                    ]}
                    data={creditList}
                />
                <StInfoLine>{t('yourLocalContent.yourTitles.creditInfo')}</StInfoLine>
            </StDropdownContainer>
        </GenericDropdown>
    );
};
